// Procedural rendering library — per-tribe visual spec.
//
// Source of truth for how each tribe renders cards/heroes. Pairs with
// docs/visual-conventions.md (rules) and src/theme/tokens.ts (palette).
//
// Critical rule: accessory > body at card-portrait scale. The signature
// object identifies the tribe before initials or stats are read.

import type { TribeId } from '../../game/data/cards/types';

/** Body posture preset — informs initials placement + tape-strip angle. */
export type BodyPosture =
  | 'planted'    // grounded, arms at sides — La Clica, Anclas, Bajitos-establishment
  | 'kinetic'    // mid-motion, elbow raised — Slameros
  | 'forward'    // slight lean — Plaqueros (writer's posture)
  | 'laidback';  // hands behind head / pockets — Bajitos-street

/** Where the accessory anchors on the card portrait. */
export type AccessoryAnchor =
  | 'top-third'      // default — players scan tops first
  | 'top-center'     // for headwear-style accessories
  | 'right-edge';    // for handheld props (guitar, mic stand)

export interface TribeVisualSpec {
  id: TribeId;
  /** Primary palette color (background gradient peak). Reads from tokens.ts. */
  paletteHexPrimary: string;
  /** Accent palette color (tape strip, accessory highlight). */
  paletteHexAccent: string;
  /** Body posture for initials placement. */
  posture: BodyPosture;
  /** Accessory primitive id — looked up in accessoryRegistry. */
  accessoryId: AccessoryId;
  /** Where the accessory sits on the card portrait. */
  accessoryAnchor: AccessoryAnchor;
  /** Optional secondary accessory for tier-3+ cards (more detail at higher tiers). */
  secondaryAccessoryId?: AccessoryId;
}

/**
 * Accessory primitive identifiers. Each id maps to a render function in
 * accessoryRegistry. Adding a new accessory:
 *   1. Add id to this union
 *   2. Create the React component in primitives/
 *   3. Register in accessoryRegistry.ts
 */
export type AccessoryId =
  | 'spraycan'         // Plaqueros — the writer's tool
  | 'hydraulic-switch' // Bajitos — the hop trigger
  | 'studded-belt'     // Slameros — pit-kid armor
  | 'paletero-bell'    // La Clica — the cart bell
  | 'mic-stand'        // La Tocada — the gig instrument
  | 'clipboard-keys'   // Anclas — the scene-keeper props
  // Secondary / tier-3+
  | 'pendleton-pattern'
  | 'hoop-earrings'
  | 'tejana-brim'
  | 'chrome-rim'
  | 'guitar-strat'
  | 'taquero-tongs'
  | 'frutero-umbrella'
  | 'elotero-pot';

/**
 * The 6 tribe specs. Palette hex values mirror src/theme/tokens.ts —
 * keep them in sync (the token file is single-source for Tailwind).
 */
export const TRIBE_VISUAL_SPECS: Record<TribeId, TribeVisualSpec> = {
  // === Cholos ===
  plaqueros: {
    id: 'plaqueros',
    paletteHexPrimary: '#c0c4ca', // spray-can chrome (tokens.colors.plaqueros)
    paletteHexAccent: '#6b2d8c',  // candy purple — the spray flick
    posture: 'forward',
    accessoryId: 'spraycan',
    accessoryAnchor: 'top-third',
    secondaryAccessoryId: 'pendleton-pattern',
  },
  la_clica: {
    id: 'la_clica',
    paletteHexPrimary: '#ff8a3d', // sodium-lamp orange (tokens.colors.laClica)
    paletteHexAccent: '#d4a945',  // tecate gold
    posture: 'planted',
    accessoryId: 'paletero-bell',
    accessoryAnchor: 'top-third',
    secondaryAccessoryId: 'hoop-earrings',
  },
  bajitos: {
    id: 'bajitos',
    paletteHexPrimary: '#d4a945', // tecate gold (tokens.colors.bajitos)
    paletteHexAccent: '#c0c4ca',  // chrome silver
    posture: 'laidback',
    accessoryId: 'hydraulic-switch',
    accessoryAnchor: 'top-third',
    secondaryAccessoryId: 'chrome-rim',
  },

  // === Emos ===
  slameros: {
    id: 'slameros',
    paletteHexPrimary: '#e6342a', // aerosol red (tokens.colors.slameros)
    paletteHexAccent: '#141008',  // sharpie black
    posture: 'kinetic',
    accessoryId: 'studded-belt',
    accessoryAnchor: 'top-third',
  },
  la_tocada: {
    id: 'la_tocada',
    paletteHexPrimary: '#ff2e7e', // stage-light hot pink (tokens.colors.laTocada)
    paletteHexAccent: '#6b2d8c',  // candy purple — stage gel
    posture: 'planted',
    accessoryId: 'mic-stand',
    accessoryAnchor: 'right-edge',
    secondaryAccessoryId: 'guitar-strat',
  },
  anclas: {
    id: 'anclas',
    paletteHexPrimary: '#5fd17e', // backyard string-light green (tokens.colors.anclas)
    paletteHexAccent: '#c8b89a',  // marker beige — domestic warmth
    posture: 'planted',
    accessoryId: 'clipboard-keys',
    accessoryAnchor: 'top-third',
  },
};

/** Convenience lookup. Returns undefined for neutral cards. */
export function getTribeVisualSpec(tribe: TribeId | undefined): TribeVisualSpec | undefined {
  if (!tribe) return undefined;
  return TRIBE_VISUAL_SPECS[tribe];
}
